// Редактор меню: категории, товары, стоп-лист по точке.
// Удаления нет — только архив: товар из старых чеков должен находиться.
import { useRef, useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { ProductDialog, draftFrom } from './ProductDialog';
import { MenuTable } from './MenuTable';
import { CategoryDialog, catDraftFrom, type CatDraft } from './CategoryDialog';
import { downloadCsv } from './csv';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money, LocationsList } from '@dastarhan/contracts';
import { formatMoney } from '@dastarhan/core';
import { z } from 'zod';

const API = '/api/v1';

const AdminMenu = z.object({
  categories: z.array(z.object({
    id: z.string(), name: z.string(), color: z.string().nullable(),
    station: z.string().nullable().catch(null),
    sort: z.number(), isActive: z.boolean(),
    /** «Бар → Напитки»: null у самой группы */
    parentId: z.string().nullable().catch(null),
    /** наценка группы, %; null — брать у родителя */
    markupPct: z.number().int().nullable().catch(null),
    /** запрет скидок: касса не даст скидку на товары группы */
    allowDiscount: z.boolean().catch(true),
    writeOffOnSale: z.boolean().catch(true),
  })),
  products: z.array(z.object({
    id: z.string(), categoryId: z.string(), name: z.string(),
    price: z.number(), unit: z.string(), isActive: z.boolean(),
    modifierGroupIds: z.array(z.string()),
    sku: z.string().nullable().catch(null),
    barcode: z.string().nullable().catch(null),
    minStock: z.number().nullable().catch(null),
    isPurchased: z.boolean().catch(true),
    /** склад и сырьё: по ним определяется тип позиции */
    trackStock: z.boolean().catch(true),
    isIngredient: z.boolean().catch(false),
    abv: z.number().nullable().catch(null),
    /** есть ли техкарта: блюдо без неё не списывает сырьё */
    hasRecipe: z.boolean().catch(false),
    cost: Money.nullable().catch(null),
    photoUrl: z.string().nullable().catch(null),
    description: z.string().nullable().catch(null),
    badges: z.string().nullable().catch(null),
  })),
});
const StopMap = z.object({ stopList: z.record(z.string(), z.number().nullable()) });

/*
 * Стоп-лист позиции.
 *
 * Две разные истории под одной кнопкой. Если у блюда есть техкарта,
 * остаток считать руками бессмысленно — он вытекает из склада:
 * показываем расчёт и предлагаем только «убрать совсем». Если
 * техкарты нет (покупные напитки, сигареты, снеки), никто кроме
 * человека остаток не знает — даём вписать число, и касса сама
 * поставит блюдо в стоп, когда счётчик дойдёт до нуля.
 */
function StopDialog({ name, hasRecipe, stopped, remaining, onClose, onApply }: {
  name: string;
  hasRecipe: boolean;
  stopped: boolean;
  remaining: number | null | undefined;
  onClose: () => void;
  onApply: (b: { off?: boolean; remaining?: number | null }) => void;
}) {
  const [left, setLeft] = useState(
    remaining === null || remaining === undefined ? '' : String(remaining),
  );
  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true">
      <div className="dialog">
        <span className="eyebrow">Стоп-лист</span>
        <h3>{name}</h3>
        {hasRecipe ? (
          <>
            <p className="hint">
              У позиции есть техкарта — остаток считается сам, по приходам
              сырья. Когда сырьё закончится, блюдо уйдёт в стоп автоматически.
            </p>
            {stopped && remaining !== null && remaining !== undefined && (
              <p className="hint">Сейчас хватает на {remaining} порц.</p>
            )}
          </>
        ) : (
          <>
            <p className="hint">
              Техкарты нет, поэтому остаток знает только человек. Впишите,
              сколько штук осталось — касса будет вычитать проданное и
              поставит позицию в стоп на нуле. Пустое поле — просто стоп.
            </p>
            <label className="field-row">
              <span>Осталось, шт</span>
              <input inputMode="numeric" value={left} placeholder="напр. 12"
                onChange={(e) => setLeft(e.target.value.replace(/\D/g, '').slice(0, 4))} />
            </label>
          </>
        )}
        <div className="dialog-actions">
          <button className="btn" onClick={onClose}>Отмена</button>
          {stopped && (
            <button className="btn" onClick={() => { onApply({ off: true }); onClose(); }}>
              Снять со стопа
            </button>
          )}
          {!hasRecipe && (
            <button className="btn primary" disabled={left === ''}
              onClick={() => { onApply({ remaining: Number(left) }); onClose(); }}>
              Сохранить остаток
            </button>
          )}
          <button className="btn danger"
            onClick={() => { onApply({ remaining: null }); onClose(); }}>
            {hasRecipe ? 'Убрать до конца дня' : 'Закончилось совсем'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MenuScreen({ token }: { token: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  // Правила группы: наценка, скидки, списание. Одно окно и для новой
  // подгруппы, и для правки существующей — форма та же
  const [groupFor, setGroupFor] = useState<{
    mode: 'sub' | 'edit';
    parentId: string | null;
    id?: string;
    name: string;
    markupPct: string;
    allowDiscount: boolean;
    writeOffOnSale: boolean;
  } | null>(null);
  const qc = useQueryClient();
  const h = { Authorization: `Bearer ${token}` };
  const jh = { ...h, 'Content-Type': 'application/json' };
  const fileRef = useRef<HTMLInputElement>(null);

  const menu = useQuery({
    queryKey: ['admin-menu'],
    queryFn: async () => AdminMenu.parse(await (await fetch(`${API}/menu/admin`, { headers: h })).json()),
  });
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => LocationsList.parse(await (await fetch(`${API}/menu/locations`, { headers: h })).json()),
  });
  const [locationId, setLocationId] = useState<string | null>(null);
  const loc = locationId ?? locations.data?.[0]?.id ?? null;

  const stops = useQuery({
    queryKey: ['stops', loc],
    enabled: !!loc,
    queryFn: async () =>
      StopMap.parse(await (await fetch(`${API}/menu/catalog?locationId=${loc}`, { headers: h })).json()).stopList,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin-menu'] });
    void qc.invalidateQueries({ queryKey: ['stops'] });
  };

  const addCategory = useMutation({
    mutationFn: (b: {
      name: string; parentId?: string | null; markupPct?: number | null;
      allowDiscount?: boolean; writeOffOnSale?: boolean;
    }) =>
      fetch(`${API}/menu/categories`, { method: 'POST', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const addProduct = useMutation({
    mutationFn: (b: { categoryId: string; name: string; price: number }) =>
      fetch(`${API}/menu/products`, { method: 'POST', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const patchProduct = useMutation({
    mutationFn: ({ id, ...b }: { id: string; photoUrl?: string | null; description?: string | null; badges?: string | null; price?: number; isActive?: boolean }) =>
      fetch(`${API}/menu/products/${id}`, { method: 'PATCH', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const setStop = useMutation({
    mutationFn: (b: { productId: string; off?: boolean; remaining?: number | null }) =>
      fetch(`${API}/menu/stop`, { method: 'POST', headers: jh, body: JSON.stringify({ locationId: loc, ...b }) }),
    onSuccess: refresh,
  });

  const [catName, setCatName] = useState('');
  const [draft, setDraft] = useState<{ categoryId: string; name: string; price: string } | null>(null);
  // Карточка номенклатуры: всё о позиции в одном окне.
  // Тип берём из строки списка — он и приезжает с сервера
  const [cardFor, setCardFor] = useState<Parameters<typeof draftFrom>[0] | null>(null);
  const [treeView, setTreeView] = useState(true);
  // Какая позиция сейчас в окне стоп-листа
  const [stopFor, setStopFor] = useState<
    { id: string; name: string; hasRecipe: boolean } | null>(null);
  // Новая позиция: категория берётся из выбранной ветки — человек
  // стоит в «Напитках» и заводит напиток, а не ищет её в списке
  const [newIn, setNewIn] = useState<string | null | undefined>(undefined);
  // Категория правится в окне, а не в подсказке браузера: у неё есть
  // цех, наценка, родитель и цвет
  const [catDraft, setCatDraft] = useState<CatDraft | null>(null);

  if (menu.isPending) return <p className="empty">Загружаем меню…</p>;
  if (menu.isError || !menu.data) return <p className="empty">Не удалось загрузить меню. Обновите страницу.</p>;

  const { categories, products } = menu.data;


  /*
   * Два вида на выбор.
   *
   * Дерево удобно, когда категорий много и работа идёт по одной ветке.
   * Плоский список — когда позиций два десятка и хочется видеть всё
   * разом, а ещё в нём живут перенос, стоп-лист и загрузка из файла.
   * Отнимать привычный вид ради нового было бы грубо.
   */
  if (treeView) {
    return (
      <>
        <div className="menu-toolbar">
          <button className="btn small accent" onClick={() => setTreeView(true)}>Папки</button>
          <button className="btn small" onClick={() => setTreeView(false)}>Список</button>
        </div>

        <MenuTable
          cats={categories.map((c) => ({
            id: c.id, name: c.name, parentId: c.parentId ?? null,
            station: c.station, sort: c.sort, isActive: c.isActive,
          }))}
          rows={products.map((p) => ({
            id: p.id, name: p.name, sku: p.sku, categoryId: p.categoryId,
            price: p.price, unit: p.unit, minStock: p.minStock,
            isActive: p.isActive, trackStock: p.trackStock,
            isIngredient: p.isIngredient, photoUrl: p.photoUrl,
            hasRecipe: p.hasRecipe, modCount: p.modifierGroupIds.length,
          }))}
          onAddCat={(parentId) => setCatDraft(catDraftFrom(undefined, parentId))}
          onEditCat={(c) => {
            const full = categories.find((x) => x.id === c.id);
            if (full) setCatDraft(catDraftFrom(full as never));
          }}
          onDeleteCat={(c) => void (async () => {
            const a = await ask({
              title: `Убрать категорию «${c.name}»`,
              sub: 'Категория исчезнет из меню. Позиции внутри неё останутся в архиве.',
              effects: [['Категория', c.name], ['Чеки и отчёты', 'сохранятся']],
              danger: true,
              confirmLabel: 'Убрать',
            });
            if (!a) return;
            const r = await fetch(`${API}/menu/categories/${c.id}`, { method: 'DELETE', headers: jh });
            if (!r.ok) {
              // Сервер объясняет, почему нельзя: «в категории 12 позиций»
              // понятнее, чем «ошибка удаления»
              const d = await r.json().catch(() => ({})) as { reason?: string };
              note(d.reason ?? 'Не удалось убрать категорию', 'bad');
              return;
            }
            void qc.invalidateQueries({ queryKey: ['admin-menu'] });
          })()}
          onCleanEmpty={(list) => void (async () => {
            const a = await ask({
              title: `Убрать ${list.length} пустых категорий`,
              sub: 'В них нет ни одной позиции — меню станет короче.',
              list: {
                title: 'Что исчезнет',
                rows: list.slice(0, 8).map((c) => c.name),
                more: list.length > 8 ? `…и ещё ${list.length - 8}` : undefined,
              },
              danger: true,
              confirmLabel: `Убрать ${list.length}`,
            });
            if (!a) return;
            await Promise.all(list.map((c) => fetch(`${API}/menu/categories/${c.id}`, {
              method: 'DELETE', headers: jh,
            })));
            void qc.invalidateQueries({ queryKey: ['admin-menu'] });
            note(`Убрано категорий: ${list.length}`);
          })()}
          onMove={(ids, categoryId) => {
            void Promise.all(ids.map((id) => fetch(`${API}/menu/products/${id}`, {
              method: 'PATCH', headers: jh, body: JSON.stringify({ categoryId }),
            }))).then(() => qc.invalidateQueries({ queryKey: ['admin-menu'] }));
          }}
          onOpen={(row) => setCardFor(products.find((x) => x.id === row.id) ?? null)}
          onAdd={(categoryId) => setNewIn(categoryId)}
          onArchive={(row) => void (async () => {
            const back = !row.isActive;
            if (!back) {
              const a = await ask({
                title: `Убрать «${row.name}» из меню`,
                effects: [
                  ['Позиция', row.name],
                  ['На кассе', 'исчезнет сразу'],
                  ['Отчёты и чеки', 'останутся как есть'],
                  ['Вернуть', 'тем же нажатием в архиве'],
                ],
                danger: true,
                confirmLabel: 'Убрать из меню',
              });
              if (!a) return;
            }
            void fetch(`${API}/menu/products/${row.id}`, {
              method: back ? 'PATCH' : 'DELETE',
              headers: jh,
              ...(back ? { body: JSON.stringify({ isActive: true }) } : {}),
            }).then(() => qc.invalidateQueries({ queryKey: ['admin-menu'] }));
          })()}
        />

        {catDraft && (
          <CategoryDialog token={token}
            initial={catDraft}
            categories={categories.map((c) => ({
              id: c.id, name: c.name, parentId: c.parentId ?? null,
            }))}
            /* цеха фиксированы контрактом: kitchen и bar — доступны
               и в пустом меню, а не только после первой категории */
            stations={['kitchen', 'bar']}
            onClose={() => setCatDraft(null)}
            onSaved={() => void qc.invalidateQueries({ queryKey: ['admin-menu'] })} />
        )}

        {(cardFor || newIn !== undefined) && (
          <ProductDialog token={token}
            initial={cardFor ? draftFrom(cardFor) : draftFrom(undefined, newIn ?? null)}
            categories={categories.map((c) => ({
              id: c.id, name: c.name, parentId: c.parentId ?? null,
            }))}
            onClose={() => { setCardFor(null); setNewIn(undefined); }}
            onSaved={() => void qc.invalidateQueries({ queryKey: ['admin-menu'] })} />
        )}
      </>
    );
  }

  return (
    <>
    <div className="menu-toolbar">
      <button className="btn small" onClick={() => setTreeView(true)}>Дерево</button>
      <button className="btn small accent" onClick={() => setTreeView(false)}>Список</button>
    </div>
    <div className="menu-edit">
      <input type="file" accept=".csv,text/csv" style={{ display: 'none' }}
        ref={fileRef}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          const reader = new FileReader();
          reader.onload = () => {
            const text = String(reader.result ?? '');
            const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
            const sep = (lines[0] ?? '').includes(';') ? ';' : ',';
            const rows = lines
              .filter((l, i) => !(i === 0 && /кат|назв|cat|name/i.test(l)))
              .map((l) => l.split(sep).map((c) => c.trim().replace(/^"|"$/g, '')))
              .filter((c) => c.length >= 3 && c[0] && c[1])
              .slice(0, 500)
              .map((c) => {
                const unitRaw = (c[3] ?? '').toLowerCase();
                const unit = unitRaw.startsWith('кг') ? 'KG'
                  : unitRaw.startsWith('л') ? 'L'
                  : unitRaw.startsWith('порц') ? 'PORTION' : 'PIECE';
                return {
                  category: c[0]!,
                  name: c[1]!,
                  price: Math.round(Number((c[2] ?? '0').replace(/\s/g, '').replace(',', '.')) * 100),
                  unit,
                };
              })
              .filter((r) => Number.isFinite(r.price) && r.price >= 0);
            if (rows.length === 0) {
              note('Не нашли строк формата: Категория; Название; Цена; Ед (шт/кг/л/порц)', 'bad');
              return;
            }
            void fetch(`${API}/menu/import`, {
              method: 'POST', headers: jh, body: JSON.stringify({ rows }),
            }).then(async (res) => {
              if (!res.ok) { note('Импорт не прошёл — проверьте файл', 'bad'); return; }
              const d = (await res.json()) as { createdCategories: number; createdProducts: number; skipped: number };
              note(`Импорт готов: категорий +${d.createdCategories}, позиций +${d.createdProducts}, пропущено дублей: ${d.skipped}`);
              refresh();
            });
          };
          reader.readAsText(f, 'utf-8');
        }} />
      {categories.filter((c) => c.isActive).length === 0 && (
        <section className="menu-cat" style={{ marginBottom: 14 }}>
          <div className="promo-form">
            <h2 style={{ margin: 0 }}>Начните с примера</h2>
            <p className="hint-inline" style={{ margin: 0 }}>
              Загрузим готовое меню (6 категорий, 18 позиций в тенге) — переименуете под себя
              быстрее, чем наберёте с нуля. Убрать потом можно архивом.
            </p>
            <button className="btn accent" style={{ alignSelf: 'flex-start' }} onClick={() => {
              void fetch(`${API}/menu/seed-demo`, { method: 'POST', headers: jh })
                .then((r) => { if (!r.ok) note('Меню уже не пустое', 'bad'); refresh(); });
            }}>Загрузить пример меню</button>
          </div>
        </section>
      )}
      <div className="menu-toolbar">
        <button className="btn" title="Категория · блюдо · цена · в наличии — под импорт карточек 2ГИС/Яндекс"
          onClick={() => {
            const catBy = new Map(categories.map((c) => [c.id, c.name] as const));
            const rows = products
              .filter((p) => p.isActive)
              .map((p) => [
                catBy.get(p.categoryId ?? '') ?? 'Прочее',
                p.name,
                (p.price / 100).toFixed(0),
                'KZT',
                stops.data && p.id in stops.data ? 'нет' : 'да',
              ]);
            if (rows.length === 0) { note('Активных позиций нет — нечего выгружать', 'bad'); return; }
            downloadCsv('меню_для_карт.csv',
              [['Категория', 'Блюдо', 'Цена', 'Валюта', 'В наличии'], ...rows]);
          }}>CSV для карт</button>
        <button className="btn" title="Прайс на печать: браузер сохранит и в PDF"
          onClick={() => {
            const cats = categories.filter((c) => c.isActive);
            const html = cats.map((c) => {
              const ps = products.filter((p) => p.categoryId === c.id && p.isActive);
              if (ps.length === 0) return '';
              return `<h2>${c.name}</h2><table>${ps.map((p) => `<tr>
                <td class="n">${p.name}${p.badges ? ` <small>${p.badges}</small>` : ''}
                  ${p.description ? `<br><em>${p.description}</em>` : ''}</td>
                <td class="p">${(p.price / 100).toLocaleString('ru-RU')} ₸</td></tr>`).join('')}</table>`;
            }).join('');
            const w = window.open('', '_blank');
            if (!w) { note('Браузер закрыл окно печати — разрешите всплывающие окна для этого сайта', 'bad'); return; }
            w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Прайс</title><style>
              body { font-family: Georgia, serif; margin: 28px; color: #222; }
              h1 { text-align: center; font-size: 26px; margin: 0 0 4px; }
              .sub { text-align: center; color: #777; margin: 0 0 22px; font-size: 13px; }
              h2 { border-bottom: 1px solid #999; padding-bottom: 4px; margin: 20px 0 8px; font-size: 18px; }
              table { width: 100%; border-collapse: collapse; }
              td { padding: 5px 0; vertical-align: top; }
              td.p { text-align: right; white-space: nowrap; width: 110px; font-weight: 700; }
              em { color: #666; font-size: 12px; font-style: normal; }
              small { font-size: 12px; }
              </style></head><body>
              <h1>Меню</h1><p class="sub">${new Date().toLocaleDateString('ru-RU')} · цены в тенге</p>
              ${html}<script>window.onload = () => window.print();</script></body></html>`);
            w.document.close();
          }}>Прайс 🖨</button>
        <form className="row" onSubmit={(e) => {
          e.preventDefault();
          if (catName.trim()) { addCategory.mutate({ name: catName.trim() }); setCatName(''); }
        }}>
          <button className="btn" type="button" title="CSV: Категория; Название; Цена; Ед"
            onClick={() => fileRef.current?.click()}>Импорт CSV</button>
          <button className="btn small" title="Выгрузка в формате импорта: category;name;price;unit"
            onClick={() => {
              // Круг замкнулся: экспорт ест тот же формат, что импорт
              const catBy = new Map(categories.map((c) => [c.id, c.name] as const));
              const lines = ['category;name;price;unit'];
              for (const p of products.filter((x) => x.isActive)) {
                const cat = (catBy.get(p.categoryId) ?? '').replaceAll(';', ',');
                lines.push(`${cat};${p.name.replaceAll(';', ',')};${(p.price / 100).toFixed(2)};${p.unit}`);
              }
              const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = 'menu-export.csv';
              a.click();
              URL.revokeObjectURL(a.href);
            }}>Экспорт CSV</button>
          <input placeholder="Новая категория…" value={catName} onChange={(e) => setCatName(e.target.value)} />
          <button className="btn" type="submit" disabled={!catName.trim()}>Добавить</button>
        </form>
        {locations.data && locations.data.length > 1 && (
          <label className="row">Стоп-лист точки
            <select value={loc ?? ''} onChange={(e) => setLocationId(e.target.value)}>
              {locations.data.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
        )}
      </div>

      {categories.length === 0 && (
        <p className="empty">Начните с категории — например, «Горячее». Товары добавляются внутрь неё.</p>
      )}

      {categories.map((c) => {
        const rows = products.filter((p) => p.categoryId === c.id);
        return (
          <section key={c.id} className="menu-cat">
            <header>
              <i style={{ background: c.color ?? 'var(--line)' }} />
              <h2>
                <button className="name-btn" title="Переименовать" onClick={() => void (async () => {
                  const a = await ask({
                    title: 'Название категории',
                    value: { label: 'Как называется', initial: c.name },
                    confirmLabel: 'Переименовать',
                  });
                  if (!a || a.value === c.name) return;
                  void fetch(`${API}/menu/categories/${c.id}`, {
                    method: 'PATCH', headers: jh, body: JSON.stringify({ name: a.value }),
                  }).then(refresh);
                })()}>{c.name}</button>
              </h2>
              {/* Подгруппа внутри группы: «Бар → Напитки». Товары можно
                  класть и в группу, и в подгруппу — владелец решает сам */}
              {!c.parentId && (
                <button className="btn small" title="Добавить подгруппу"
                  onClick={() => setGroupFor({
                    mode: 'sub', parentId: c.id, name: '',
                    markupPct: c.markupPct != null ? String(c.markupPct) : '',
                    allowDiscount: c.allowDiscount ?? true,
                    writeOffOnSale: c.writeOffOnSale ?? true,
                  })}>
                  + Подгруппа
                </button>
              )}
              <button className={`btn small ${c.markupPct != null || c.allowDiscount === false ? 'accent' : ''}`}
                title="Наценка, скидки, списание"
                onClick={() => setGroupFor({
                  mode: 'edit', parentId: c.parentId ?? null, id: c.id, name: c.name,
                  markupPct: c.markupPct != null ? String(c.markupPct) : '',
                  allowDiscount: c.allowDiscount ?? true,
                  writeOffOnSale: c.writeOffOnSale ?? true,
                })}>
                Правила{c.markupPct != null ? ` · +${c.markupPct}%` : ''}
                {c.allowDiscount === false ? ' · без скидок' : ''}
              </button>
              <button className="btn small" title="Выше в меню и на кассе"
                disabled={categories.indexOf(c) === 0}
                onClick={() => {
                  // Порядок — это два PATCH: соседи меняются местами
                  const i = categories.indexOf(c);
                  const prev = categories[i - 1];
                  if (!prev) return;
                  void Promise.all([
                    fetch(`${API}/menu/categories/${c.id}`, {
                      method: 'PATCH', headers: jh, body: JSON.stringify({ sort: i - 1 }) }),
                    fetch(`${API}/menu/categories/${prev.id}`, {
                      method: 'PATCH', headers: jh, body: JSON.stringify({ sort: i }) }),
                  ]).then(refresh);
                }}>↑</button>
              <button className="btn small" title="Ниже в меню и на кассе"
                disabled={categories.indexOf(c) === categories.length - 1}
                onClick={() => {
                  const i = categories.indexOf(c);
                  const next = categories[i + 1];
                  if (!next) return;
                  void Promise.all([
                    fetch(`${API}/menu/categories/${c.id}`, {
                      method: 'PATCH', headers: jh, body: JSON.stringify({ sort: i + 1 }) }),
                    fetch(`${API}/menu/categories/${next.id}`, {
                      method: 'PATCH', headers: jh, body: JSON.stringify({ sort: i }) }),
                  ]).then(refresh);
                }}>↓</button>
              <button className="btn small"
                title="Застопить всю категорию на выбранной точке — до конца дня"
                onClick={() => void (async () => {
                  const live = rows.filter((p) => p.isActive);
                  if (live.length === 0) return;
                  const a = await ask({
                    title: `Застопить «${c.name}» до конца дня`,
                    sub: 'Позиции исчезнут с кассы прямо сейчас. Полночь снимет стоп сама.',
                    effects: [
                      ['Категория', c.name],
                      ['Уйдёт с кассы', `${live.length} позиций`],
                      ['Вернутся', 'автоматически в полночь'],
                    ],
                    list: {
                      title: 'Что уйдёт',
                      rows: live.slice(0, 6).map((p) => p.name),
                      more: live.length > 6 ? `…и ещё ${live.length - 6}` : undefined,
                    },
                    danger: true,
                    confirmLabel: 'Застопить всё',
                  });
                  if (!a) return;
                  // Печь встала — вся «Выпечка» уходит со витрины разом
                  await Promise.all(live.map((p) => setStop.mutateAsync({ productId: p.id })));
                  note('Категория на стопе. Полночь снимет.');
                })()}>🚫 всё</button>
              <button className="btn small"
                title="Цех бегунка: при оплате позиции категории печатаются на принтер цеха"
                onClick={() => {
                  const next = c.station === 'kitchen' ? 'bar' : c.station === 'bar' ? null : 'kitchen';
                  void fetch(`${API}/menu/categories/${c.id}`, {
                    method: 'PATCH', headers: jh, body: JSON.stringify({ station: next }),
                  }).then(refresh);
                }}>
                {c.station === 'kitchen' ? '🍳 кухня' : c.station === 'bar' ? '🍹 бар' : 'цех: —'}
              </button>
              <button className="btn small" title="Убрать категорию в архив" onClick={() => void (async () => {
                /* Куда переедут позиции — выбор из списка, а не номер строки:
                   ошибка в цифре уводила блюда в чужую категорию молча. */
                const others = categories.filter((x) => x.id !== c.id && x.isActive);
                const a = await ask({
                  title: `Убрать «${c.name}» в архив`,
                  sub: others.length
                    ? 'Категория исчезнет из меню. Решите, что делать с её позициями.'
                    : 'Переносить некуда — позиции уйдут в архив вместе с категорией.',
                  effects: [
                    ['Категория', c.name],
                    ['Позиций внутри', String(rows.length)],
                    ['Чеки и отчёты', 'сохранятся полностью'],
                  ],
                  ...(others.length ? {
                    choices: {
                      label: 'Куда перенести позиции',
                      initial: 'archive',
                      options: [
                        { id: 'archive', title: 'В архив вместе с категорией', note: 'вернуть можно в любой момент' },
                        ...others.map((x) => ({ id: x.id, title: x.name, note: 'позиции переедут сюда' })),
                      ],
                    },
                  } : {}),
                  danger: true,
                  confirmLabel: 'Убрать в архив',
                });
                if (!a) return;
                const moveTo = a.choice && a.choice !== 'archive' ? a.choice : null;
                void fetch(`${API}/menu/categories/${c.id}`, {
                  method: 'PATCH', headers: jh,
                  body: JSON.stringify({ isActive: false, moveProductsTo: moveTo }),
                }).then(refresh);
              })()}>В архив</button>
              <button className="btn small" onClick={() => setDraft({ categoryId: c.id, name: '', price: '' })}>
                + Товар
              </button>
            </header>

            {draft?.categoryId === c.id && (
              <form className="menu-row draft" onSubmit={(e) => {
                e.preventDefault();
                const price = Math.round(Number(draft.price.replace(',', '.')) * 100);
                if (draft.name.trim() && price > 0) {
                  addProduct.mutate({ categoryId: c.id, name: draft.name.trim(), price });
                  setDraft(null);
                }
              }}>
                <input autoFocus placeholder="Название" value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                <input placeholder="Цена, ₸" inputMode="decimal" value={draft.price}
                  onChange={(e) => setDraft({ ...draft, price: e.target.value })} />
                <button className="btn small" type="submit">Сохранить</button>
                <button className="btn small" type="button" onClick={() => setDraft(null)}>Отмена</button>
              </form>
            )}

            {rows.map((p) => {
              const stopped = stops.data ? p.id in stops.data : false;
              return (
                <div key={p.id} className={`menu-row ${p.isActive ? '' : 'archived'}`}>
                  {/* Имя открывает карточку: раньше правились только имя
                      и цена прямо в строке, а за составом и фото надо
                      было идти на другие экраны */}
                  <button className="name menu-open" onClick={() => setCardFor(p)}>
                    {p.name}{!p.isActive && ' · в архиве'}
                  </button>
                  <button className="btn small" title="Дубликат: новое блюдо из похожего"
                    onClick={() => void (async () => {
                      const a = await ask({
                        title: 'Новое блюдо из похожего',
                        sub: `Настройки «${p.name}» скопируются: цена, единица, описание, бейджи.`,
                        value: { label: 'Название нового блюда', initial: `${p.name} (новое)` },
                        confirmLabel: 'Создать',
                      });
                      if (!a) return;
                      const nm = a.value;
                      void fetch(`${API}/menu/products`, {
                        method: 'POST', headers: jh,
                        body: JSON.stringify({
                          categoryId: p.categoryId, name: nm, price: p.price, unit: p.unit,
                          ...(p.description ? { description: p.description } : {}),
                          ...(p.badges ? { badges: p.badges } : {}),
                        }),
                      }).then(refresh);
                    })()}>⧉</button>
                  <button className="btn small" title={p.photoUrl ? 'Фото задано — изменить или убрать' : 'Фото для витрины (ссылка)'}
                    onClick={() => void (async () => {
                      const a = await ask({
                        title: `Фото «${p.name}»`,
                        sub: 'Показывается на витрине и в приложении гостя.',
                        value: {
                          label: 'Ссылка на картинку',
                          initial: p.photoUrl ?? '',
                          placeholder: 'https://…/foto.jpg',
                          allowEmpty: true,
                          hint: 'оставьте пустым, чтобы убрать фото',
                        },
                        confirmLabel: 'Сохранить',
                      });
                      if (!a) return;
                      patchProduct.mutate({ id: p.id, photoUrl: a.value || null });
                    })()}>{p.photoUrl ? '📷✓' : '📷'}</button>
                  <button className="btn small" title={p.description ? 'Описание задано — изменить' : 'Состав/граммовка для витрины'}
                    onClick={() => void (async () => {
                      const a = await ask({
                        title: `Описание «${p.name}»`,
                        sub: 'Состав и граммовка — их видит гость на витрине.',
                        value: {
                          label: 'Описание, до 200 знаков',
                          initial: p.description ?? '',
                          multiline: true,
                          allowEmpty: true,
                          placeholder: 'Тесто на закваске, томаты, моцарелла · 380 г',
                          hint: 'пусто — убрать описание',
                        },
                        confirmLabel: 'Сохранить',
                      });
                      if (!a) return;
                      patchProduct.mutate({ id: p.id, description: a.value.slice(0, 200) || null });
                    })()}>{p.description ? '✎✓' : '✎'}</button>
                  <button className="btn small" title={p.badges ? 'Бейджи витрины — изменить' : 'Бейджи: 🌶️ острое, 🥜 орехи…'}
                    onClick={() => void (async () => {
                      const a = await ask({
                        title: `Бейджи «${p.name}»`,
                        sub: 'Короткие метки на витрине: острое, орехи, новинка.',
                        value: {
                          label: 'Метки',
                          initial: p.badges ?? '',
                          allowEmpty: true,
                          placeholder: '🌶️ острое',
                          hint: 'пусто — убрать метки',
                        },
                        confirmLabel: 'Сохранить',
                      });
                      if (!a) return;
                      patchProduct.mutate({ id: p.id, badges: a.value || null });
                    })()}>{p.badges ? '🏷✓' : '🏷'}</button>
                  <button className="price money" title="Изменить цену"
                    onClick={() => void (async () => {
                      const a = await ask({
                        title: `Цена «${p.name}»`,
                        effects: [
                          ['Сейчас', formatMoney(p.price)],
                          ...(p.cost !== null
                            ? [['Себестоимость', formatMoney(p.cost)] as [string, string]]
                            : []),
                        ],
                        value: {
                          label: 'Новая цена, ₸',
                          initial: String(p.price / 100),
                          numeric: true,
                          hint: p.cost !== null
                            ? `ниже ${formatMoney(p.cost)} блюдо уйдёт в минус`
                            : 'цена на кассе изменится сразу',
                        },
                        confirmLabel: 'Изменить цену',
                      });
                      if (!a) return;
                      const price = Math.round(Number(a.value.replace(',', '.')) * 100);
                      if (price > 0) patchProduct.mutate({ id: p.id, price });
                      else note('Цена должна быть больше нуля — не сохранил', 'bad');
                    })()}>
                    {formatMoney(p.price)}{p.cost !== null && p.price > 0 && (() => {
                      const mg = Math.round(((p.price - p.cost) / p.price) * 100);
                      return (
                        <em className={`hint-inline ${mg < 15 ? 'margin-thin' : ''}`}
                          title={`Себестоимость ${formatMoney(p.cost)} · маржа ${mg}%${mg < 15 ? ' — худая!' : ''}`}>
                          {' '}{mg}%{mg < 15 ? ' ⚠' : ''}
                        </em>
                      );
                    })()}
                  </button>
                  <button className={`btn small ${stopped ? 'danger' : ''}`} disabled={!loc}
                    onClick={() => setStopFor({
                      id: p.id, name: p.name, hasRecipe: p.hasRecipe === true,
                    })}>
                    {stopped
                      ? (stops.data?.[p.id] ? `Осталось ${stops.data[p.id]}` : 'Снять со стопа')
                      : 'Стоп-лист'}
                  </button>
                  <button className="btn small"
                    onClick={() => patchProduct.mutate({ id: p.id, isActive: !p.isActive })}>
                    {p.isActive ? 'В архив' : 'Вернуть'}
                  </button>
                </div>
              );
            })}
            {rows.length === 0 && draft?.categoryId !== c.id && (
              <p className="cat-empty">Пока пусто — добавьте первый товар.</p>
            )}
          </section>
        );
      })}
      {categories.some((c) => !c.isActive) && (
        <section className="menu-cat" style={{ marginTop: 16 }}>
          <header><h2>Архив категорий</h2></header>
          <div className="link-row" style={{ borderTop: 0 }}>
            {categories.filter((c) => !c.isActive).map((c) => (
              <button key={c.id} className="chip" onClick={() => {
                void fetch(`${API}/menu/categories/${c.id}`, {
                  method: 'PATCH', headers: jh, body: JSON.stringify({ isActive: true }),
                }).then(refresh);
              }}>↩ {c.name}</button>
            ))}
          </div>
        </section>
      )}
    </div>
      {stopFor && (
        <StopDialog
          name={stopFor.name}
          hasRecipe={stopFor.hasRecipe}
          stopped={stops.data ? stopFor.id in stops.data : false}
          remaining={stops.data?.[stopFor.id]}
          onClose={() => setStopFor(null)}
          onApply={(b) => setStop.mutate({ productId: stopFor.id, ...b })}
        />
      )}
      {cardFor && (
        <ProductDialog token={token}
          initial={draftFrom(cardFor)}
          categories={categories.map((c) => ({
            id: c.id, name: c.name, parentId: c.parentId ?? null,
          }))}
          onClose={() => setCardFor(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['admin-menu'] })} />
      )}


      {groupFor && (
        <div className="dialog" onClick={() => setGroupFor(null)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <h2>{groupFor.mode === 'sub' ? 'Новая подгруппа' : `Правила · ${groupFor.name}`}</h2>

            {groupFor.mode === 'sub' && (
              <label>Название
                <input autoFocus value={groupFor.name} placeholder="Напитки"
                  onChange={(e) => setGroupFor({ ...groupFor, name: e.target.value })} />
              </label>
            )}

            <label>Наценка, %
              <input type="number" min={0} max={2000} inputMode="numeric" placeholder="300"
                value={groupFor.markupPct}
                onChange={(e) => setGroupFor({ ...groupFor, markupPct: e.target.value })} />
              <i className="split">
                Цена = себестоимость + наценка. Пусто — берём у группы выше
                или ставим цену вручную
              </i>
            </label>

            {/* Запрет скидок живёт у группы, а не в голове кассира.
                На алкоголь скидки часто запрещены, и помнить об этом
                в час пик невозможно */}
            <label className="check">
              <input type="checkbox" checked={groupFor.allowDiscount}
                onChange={(e) => setGroupFor({ ...groupFor, allowDiscount: e.target.checked })} />
              Разрешить скидки на товары группы
            </label>

            <label className="check">
              <input type="checkbox" checked={groupFor.writeOffOnSale}
                onChange={(e) => setGroupFor({ ...groupFor, writeOffOnSale: e.target.checked })} />
              Списывать со склада при продаже
            </label>
            <p className="hint">
              Выключают для услуг: кальян-сервис, аренда зала — там нечего
              списывать. У блюд списание идёт по техкарте, у готовых
              товаров — штукой.
            </p>

            <div className="modal-actions">
              <button className="btn" onClick={() => setGroupFor(null)}>Отмена</button>
              <button className="btn primary"
                disabled={groupFor.mode === 'sub' && groupFor.name.trim().length < 2}
                onClick={() => {
                  const markupPct = groupFor.markupPct.trim() === ''
                    ? null : Math.round(Number(groupFor.markupPct));
                  const body = {
                    markupPct,
                    allowDiscount: groupFor.allowDiscount,
                    writeOffOnSale: groupFor.writeOffOnSale,
                  };
                  if (groupFor.mode === 'sub') {
                    addCategory.mutate({
                      name: groupFor.name.trim(), parentId: groupFor.parentId, ...body,
                    });
                  } else {
                    void fetch(`${API}/menu/categories/${groupFor.id}`, {
                      method: 'PATCH', headers: jh, body: JSON.stringify(body),
                    }).then(refresh);
                  }
                  setGroupFor(null);
                }}>
                {groupFor.mode === 'sub' ? 'Создать' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
      {sheet}
      {noteView}
    </>
  );
}